/**
 * My day block: open project tasks owned by the signed-in person, grouped by PRJ.
 * Groups that contain an overdue task come first; inside a group, overdue comes before later due dates.
 * A successful empty read renders nothing.
 */
import { useQuery } from '@tanstack/react-query'
import { describeError } from '../../lib/api/client'
import type { TaskOut } from '../../lib/api/generated'
import { projectTasksQuery } from './api'
import { projectReadAbsent } from './project-mark'
import { officeToday, TaskTickRow, taskDueDay, taskIsOverdue, useTickTasks } from './task-list'

export interface TaskGroup {
  number: string
  tasks: TaskOut[]
}

export function groupMyTasks(tasks: TaskOut[], today: string): TaskGroup[] {
  const groups = new Map<string, TaskOut[]>()
  for (const task of tasks) {
    const number = task.project_number?.trim() || 'Project'
    const list = groups.get(number) ?? []
    list.push(task)
    groups.set(number, list)
  }
  const sortTasks = (list: TaskOut[]) =>
    [...list].sort((a, b) => {
      const rank = Number(taskIsOverdue(b, today)) - Number(taskIsOverdue(a, today))
      if (rank) return rank
      const ad = taskDueDay(a) ?? '9999-99-99'
      const bd = taskDueDay(b) ?? '9999-99-99'
      if (ad !== bd) return ad < bd ? -1 : 1
      return a.text.localeCompare(b.text)
    })
  return [...groups.entries()]
    .map(([number, list]) => ({
      number,
      tasks: sortTasks(list),
      overdue: list.some((task) => taskIsOverdue(task, today)),
    }))
    .sort((a, b) => Number(b.overdue) - Number(a.overdue) || a.number.localeCompare(b.number))
    .map(({ number, tasks }) => ({ number, tasks }))
}

export function MyProjectTasks() {
  const q = useQuery(projectTasksQuery({ owner: 'me', state: 'open' }))
  const tick = useTickTasks()
  if (q.isPending) return null
  if (q.isError) {
    if (projectReadAbsent(q.error)) return null
    return (
      <p role="status" className="mb-4 text-sm text-warning">
        <span>My project tasks are unreachable.</span>{' '}
        <button type="button" className="cursor-pointer underline" onClick={() => void q.refetch()}>
          Retry
        </button>
      </p>
    )
  }
  if (q.data.length === 0) return null
  const groups = groupMyTasks(q.data, officeToday())
  return (
    <section aria-label="My project tasks" className="mb-4 space-y-3 rounded-xl border border-border bg-card p-3">
      <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">My project tasks</h2>
      {groups.map((group) => (
        <div key={group.number}>
          <h3 className="text-sm font-semibold">{group.number}</h3>
          <ul className="mt-1">
            {group.tasks.map((task) => (
              <TaskTickRow key={task.id} task={task} tick={tick} />
            ))}
          </ul>
        </div>
      ))}
      {tick.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {describeError(tick.error)}
        </p>
      ) : null}
    </section>
  )
}
